import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import moment from 'moment';
import { isPlatformBrowser } from '@angular/common';
import { localStorageConstants } from '../../consts/local_storage_constants';

@Injectable({
    providedIn: 'root',
})
export class ApiService {

    constructor(private http: HttpClient, @Inject(PLATFORM_ID) private platformId: Object) { }

    /**
     * Get authentication token from localStorage based on portal type.
     */
    private getToken(): string {
        if (isPlatformBrowser(this.platformId)) {
            const portalType = localStorage.getItem(localStorageConstants.USER_TYPE);
            const token = portalType === 'portal'
                ? localStorage.getItem(localStorageConstants.PORTAL_TOKEN) || ''
                : localStorage.getItem(localStorageConstants.SUPER_ADMIN_TOKEN) || '';
            return token;
        }
        return '';
    }


    /**
     * Generate headers with or without authentication token.
     * @param authRequired Boolean to determine if Authorization is needed.
     */
    public getHeaders(authRequired: boolean): HttpHeaders {
        let headers = new HttpHeaders();
        const language = isPlatformBrowser(this.platformId) ? localStorage.getItem(localStorageConstants.LANGUAGE) || 'en' : 'en';

        if (authRequired) {
            headers = headers.set('Authorization', this.getToken());
        }

        headers = headers.set('local_offset', `${moment().utcOffset() * 60}`);
        headers = headers.set('language', language);

        return headers;
    }

    /**
     * Handle API errors.
     */
    private handleError(error: HttpErrorResponse): Observable<never> {
        let errorMessage = 'An unknown error occurred!';

        if (typeof ErrorEvent !== 'undefined' && error.error instanceof ErrorEvent) {
            // Client-side errors
            errorMessage = `${error.error.message}`;
        } else if (error.error && error.error.message) {
            // Server-side errors
            errorMessage = `${error.error.message}`;
        } else {
            errorMessage = `${error.message}`;
        }

        console.error("errorMessage", errorMessage);
        return throwError(() => new Error(errorMessage));
    }

    // ==============================
    // 🚀 API METHODS
    // ==============================

    /**
     * HTTP GET call with token.
     */
    public httpGetCall<T>(url: string, params: HttpParams): Observable<T> {
        const finalUrl = this.appendQueryParams(url, params);
        return this.http.get<T>(url, { headers: this.getHeaders(true), params: params }).pipe(
            map((res: T) => res),
            catchError(this.handleError)
        );
    }

    /**
     * HTTP GET call without
     */
    public httpGetCallWithoutParameter<T>(url: string): Observable<T> {
        return this.http.get<T>(url, { headers: this.getHeaders(true)}).pipe(
            map((res: T) => res),
            catchError(this.handleError)
        );
    }

    /**
     * HTTP GET call without token.
     */
    public httpGetCallWithoutToken<T>(url: string): Observable<T> {
        return this.http.get<T>(url, { headers: this.getHeaders(false) }).pipe(
            map((res: T) => res),
            catchError(this.handleError)
        );
    }

    /**
     * HTTP POST call with token.
     */
    public httpPostCall<T>(url: string, body: any, params?: any): Observable<T> {
        const finalUrl = this.appendQueryParams(url, params);
        return this.http.post<T>(finalUrl, body, { headers: this.getHeaders(true) }).pipe(
            map((res: T) => res),
            catchError(this.handleError)
        );
    }

    /**
     * HTTP POST call without token.
     */
    public httpPostCallWithoutToken<T>(url: string, body: any, params?: any): Observable<T> {
        const finalUrl = this.appendQueryParams(url, params);
        return this.http.post<T>(finalUrl, body, { headers: this.getHeaders(false) }).pipe(
            map((res: T) => res),
            catchError(this.handleError)
        );
    }

    /**
     * HTTP PUT call with token.
     */
    public httpPutCall<T>(url: string, body: any, params?: any): Observable<T> {
        const finalUrl = this.appendQueryParams(url, params);
        return this.http.put<T>(finalUrl, body, { headers: this.getHeaders(true) }).pipe(
            map((res: T) => res),
            catchError(this.handleError)
        );
    }

    /**
     * HTTP PUT call without token.
     */
    public httpPutCallWithoutToken<T>(url: string, body: any, params?: any): Observable<T> {
        const finalUrl = this.appendQueryParams(url, params);
        return this.http.put<T>(finalUrl, body, { headers: this.getHeaders(false) }).pipe(
            map((res: T) => res),
            catchError(this.handleError)
        );
    }

    /**
     * HTTP PATCH call with token.
     */
    public httpPatchCall<T>(url: string, body: any, params?: any): Observable<T> {
        const finalUrl = this.appendQueryParams(url, params);
        return this.http.patch<T>(finalUrl, body, { headers: this.getHeaders(true) }).pipe(
            map((res: T) => res),
            catchError(this.handleError)
        );
    }

    /**
     * HTTP PATCH call without token.
     */
    public httpPatchCallWithoutToken<T>(url: string, body: any, params?: any): Observable<T> {
        const finalUrl = this.appendQueryParams(url, params);
        return this.http.patch<T>(finalUrl, body, { headers: this.getHeaders(false) }).pipe(
            map((res: T) => res),
            catchError(this.handleError)
        );
    }

    /**
     * Append query parameters to URL if provided.
     */
    private appendQueryParams(url: string, params?: any): string {
        if (params) {
            const queryParams = new URLSearchParams(params).toString();
            return `${url}?${queryParams}`;
        }
        return url;
    }

    /**
 * HTTP DELETE call with token.
 */
public httpDeleteCall<T>(url: string, params?: HttpParams): Observable<T> {
    return this.http.delete<T>(url, {
        headers: this.getHeaders(true),
        params: params
    }).pipe(
        map((res: T) => res),
        catchError(this.handleError)
    );
}

}
